import { useEffect, useRef, useState } from 'react';

import { api, type KeresesEredmeny } from '../api.ts';

/**
 * Globális kereső a menüsáv tetején. (Megbízás 2.0 / 5.12)
 *
 * Két betűtől keres öt helyen: éttermek, cégek, számlák, sofőrök, autók.
 * A találatra kattintva a megfelelő nézetre ugrik.
 *
 * Ez a „hol is van ez?" kérdésre válasz: nem kell tudni, melyik képernyőn
 * keresd a partnert vagy a számlát, elég beírni a nevét.
 */
export function GlobalisKereso({
  onTalalat,
}: {
  onTalalat: (ugras: { kepernyo: string; etterem_id?: number; q?: string }) => void;
}) {
  const [q, setQ] = useState('');
  const [eredmeny, setEredmeny] = useState<KeresesEredmeny | null>(null);
  const [nyitva, setNyitva] = useState(false);
  const doboz = useRef<HTMLDivElement>(null);
  const mezo = useRef<HTMLInputElement>(null);
  const kerelemSorszam = useRef(0);
  /**
   * A menüsáv görgethető, ezért az abszolút pozicionált lista beleszorulna
   * a 180 pixeles szélességébe. A találatok így `position: fixed` dobozban
   * állnak, aminek a helyét a keresőmezőhöz mérjük.
   */
  const [hely, setHely] = useState<{ bal: number; teteje: number } | null>(null);

  useEffect(() => {
    if (!nyitva || !mezo.current) return;
    const frissit = () => {
      const r = mezo.current?.getBoundingClientRect();
      if (r) setHely({ bal: r.left, teteje: r.bottom + 4 });
    };
    frissit();
    window.addEventListener('resize', frissit);
    window.addEventListener('scroll', frissit, true);
    return () => {
      window.removeEventListener('resize', frissit);
      window.removeEventListener('scroll', frissit, true);
    };
  }, [nyitva]);

  useEffect(() => {
    const tiszta = q.trim();
    if (tiszta.length < 2) {
      setEredmeny(null);
      return;
    }
    const idozito = setTimeout(async () => {
      const sajat = ++kerelemSorszam.current;
      try {
        const v = await api.get<KeresesEredmeny>(
          `/api/attekintes/kereses?q=${encodeURIComponent(tiszta)}`,
        );
        // Csak a legfrissebb kérés válasza számít.
        if (sajat === kerelemSorszam.current) {
          setEredmeny(v);
          setNyitva(true);
        }
      } catch {
        if (sajat === kerelemSorszam.current) setEredmeny(null);
      }
    }, 220);
    return () => clearTimeout(idozito);
  }, [q]);

  /* Kattintás a listán kívülre: záródjon be. */
  useEffect(() => {
    function kint(e: MouseEvent) {
      if (doboz.current && !doboz.current.contains(e.target as Node)) setNyitva(false);
    }
    document.addEventListener('mousedown', kint);
    return () => document.removeEventListener('mousedown', kint);
  }, []);

  function valaszt(ugras: { kepernyo: string; etterem_id?: number; q?: string }) {
    onTalalat(ugras);
    setNyitva(false);
    setQ('');
    setEredmeny(null);
  }

  return (
    <div className="gkereso" ref={doboz}>
      <input
        ref={mezo}
        value={q}
        placeholder="keresés mindenben…"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => eredmeny && setNyitva(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setNyitva(false);
            setQ('');
          }
        }}
      />

      {nyitva && eredmeny && (
        <div
          className="gtalalat"
          style={hely ? { left: hely.bal, top: hely.teteje } : undefined}
        >
          {eredmeny.csoportok.length === 0 ? (
            <div className="gures">Nincs találat erre: „{eredmeny.q}"</div>
          ) : (
            eredmeny.csoportok.map((cs) => (
              <div key={cs.kulcs} className="gcsoport">
                <div className="gfej">
                  {cs.cimke}
                  <b>{cs.osszes}</b>
                </div>
                {cs.talalatok.map((t) => (
                  <button key={`${cs.kulcs}-${t.id}`} className="gsor" onClick={() => valaszt(t.ugras)}>
                    <span className="gnev">{t.cim}</span>
                    {t.alcim && <span className="galcim">{t.alcim}</span>}
                  </button>
                ))}
                {cs.osszes > cs.talalatok.length && (
                  <div className="gtobb">
                    …és még {cs.osszes - cs.talalatok.length} találat. Írj be többet a névből.
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
